import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import AppLoader from "@/components/AppLoader/AppLoader";
import Header from "@/components/header/Header";
import { useAuth0ManagementApi } from "@/hooks/useAuth0ManagementApi";
import { API_ENDPOINTS } from "@/apiEndpoints";
import {
  updateSupportedLanguages,
  ensureLanguageResourcesLoaded,
  registerLocaleUrls,
  setLocaleUrlRefresher,
} from "@/i18n/i18n";
import i18n from "@/i18n/i18n";
import { applyLanguagePriority } from "@/i18n/applyLanguagePriority";
import { getSupportedLanguageFromPathname } from "@/i18n/languages";
import { unwrapApiData } from "@/utils";
import {
  extractInvitationParams,
  buildInvitationAuthParams,
} from "@/auth0/invitationParamHandler";
import type {
  EnabledLocalesResponse,
  LocalesApiResponse,
  ProfileLocaleWithNameResponse,
} from "@/types/common";
import type {
  OrganizationResponse,
  OrgDataType,
} from "@/types/profileSettings";

const ProtectedRoute = () => {
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const { callManagementApi } = useAuth0ManagementApi();
  const location = useLocation();
  const [profileName, setProfileName] = useState<
    Pick<ProfileLocaleWithNameResponse["data"], "first_name" | "last_name">
  >({});
  const [orgData, setOrgData] = useState<OrgDataType>();
  const [isOrgRolesLoading, setIsOrgRolesLoading] = useState(true);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Check for organization invitation parameters in URL
      const searchParams = new URLSearchParams(location.search);
      const invitationParams = extractInvitationParams(searchParams);

      // If invitation parameters are present, initiate invitation flow
      if (invitationParams) {
        const authorizationParams = buildInvitationAuthParams(invitationParams);
        loginWithRedirect({
          authorizationParams,
          appState: { returnTo: location.pathname },
        });
      } else {
        // Standard login flow for non-invitation URLs
        loginWithRedirect({
          appState: { returnTo: location.pathname },
        });
      }
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, location.search]);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;

    const initializeLanguage = async () => {
      setIsOrgRolesLoading(true);

      try {
        const [
          tenantResult,
          localesResult,
          profileLocaleResult,
          organizationResult,
        ] = await Promise.allSettled([
          callManagementApi<EnabledLocalesResponse>(
            API_ENDPOINTS.getTenantSettings(),
            { method: "GET" },
          ),
          callManagementApi<LocalesApiResponse>(API_ENDPOINTS.getLocales(), {
            method: "GET",
          }),
          callManagementApi<ProfileLocaleWithNameResponse>(
            API_ENDPOINTS.getProfileLocaleWithName(),
            { method: "GET" },
          ),
          callManagementApi<OrganizationResponse>(
            API_ENDPOINTS.getOrganization(),
            { method: "GET" },
          ),
        ]);

        // Update supported languages from tenant settings
        if (
          tenantResult.status === "fulfilled" &&
          tenantResult.value?.data.enabled_locales?.length
        ) {
          const normalizedLocales = [
            ...new Set(
              tenantResult.value.data.enabled_locales.map(
                (locale) => locale.split("-")[0],
              ),
            ),
          ];
          updateSupportedLanguages(normalizedLocales);
        }

        // Register all locale S3 URLs for on-demand fetching.
        // Only the language actually needed will be fetched (lazily, via ensureLanguageResourcesLoaded).
        if (
          localesResult.status === "fulfilled" &&
          localesResult.value?.data?.length
        ) {
          registerLocaleUrls(localesResult.value.data);
        }

        // Register a refresher so that expired presigned S3 URLs are automatically renewed.
        setLocaleUrlRefresher(async () => {
          const freshLocalesResult =
            await callManagementApi<LocalesApiResponse>(
              API_ENDPOINTS.getLocales(),
              { method: "GET" },
            );
          if (freshLocalesResult?.data?.length) {
            registerLocaleUrls(freshLocalesResult.data);
          }
        });

        const profileLocaleWithNameData =
          profileLocaleResult.status === "fulfilled" &&
          profileLocaleResult.value
            ? unwrapApiData<ProfileLocaleWithNameResponse["data"]>(
                profileLocaleResult.value,
              )
            : undefined;

        setProfileName({
          first_name: profileLocaleWithNameData?.first_name,
          last_name: profileLocaleWithNameData?.last_name,
        });

        const userPrefferedLocale = profileLocaleWithNameData?.locale;

        if (
          organizationResult.status === "fulfilled" &&
          organizationResult.value?.data
        ) {
          const organizationData = unwrapApiData<OrganizationResponse["data"]>(
            organizationResult.value,
          );

          const orgAdminRoles = (
            Array.isArray(organizationData.org_admin_role)
              ? organizationData.org_admin_role
              : (organizationData.org_admin_role?.split(",") ?? [])
          )
            .map((role) => role.trim())
            .filter((role) => role.length > 0);

          setOrgData({
            name: organizationData.name ?? "",
            mfaRequiredFlag: organizationData.mfa_required,
            orgAdminRoles,
          });
        }

        await applyLanguagePriority(userPrefferedLocale);
      } finally {
        setIsOrgRolesLoading(false);
      }
    };

    void initializeLanguage();
  }, [isAuthenticated, user?.sub]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const pathLanguage = getSupportedLanguageFromPathname(location.pathname);
    const supportedLngs =
      (i18n.options.supportedLngs as string[] | false) || [];

    if (!pathLanguage || !supportedLngs.includes(pathLanguage)) return;
    if (i18n.resolvedLanguage === pathLanguage) return;

    void ensureLanguageResourcesLoaded(pathLanguage).then(() =>
      i18n.changeLanguage(pathLanguage),
    );
  }, [isAuthenticated, location.pathname]);

  if (!isAuthenticated) {
    return <AppLoader />;
  }

  const outletContext: OrgDataType = {
    name: orgData?.name ?? "",
    mfaRequiredFlag: orgData?.mfaRequiredFlag,
    orgAdminRoles: orgData?.orgAdminRoles ?? [],
    isOrgRolesLoading,
  };

  return (
    <>
      <Header
        firstName={profileName.first_name}
        lastName={profileName.last_name}
        organizationName={orgData?.name ? orgData.name : ""}
        orgAdminRoles={orgData?.orgAdminRoles ? orgData.orgAdminRoles : []}
      />
      <Outlet context={outletContext} />
    </>
  );
};

export default ProtectedRoute;
